/* eslint-disable camelcase */
import { KeyValueControl } from "@app-components/control/KeyValueControl";
import { PreFilterReports } from "@app-components/control/PreFilter";
import { TabularData } from "@app-components/TabularData";
import { useBroadcastChannel } from "@app-context/broadcastChannels/context";
import { useFetchCalls } from "@app-context/networkCalls/useFetchCalls";
import { useRefCallback } from "@enfusion-ui/hooks";
import { WebReportTableSync } from "@enfusion-ui/types";
import {
  Alert,
  CenterContent,
  ContentMessage,
  SecondaryTabList,
  Spinner,
  TextInput,
  VisibleIf,
} from "@enfusion-ui/web-components";
import {
  createReportColumnDefs,
  PositionFinderRowData,
  useThisTab,
  warningToast,
} from "@enfusion-ui/web-core";
import { faPaperPlane, faWarning } from "@fortawesome/pro-solid-svg-icons";
import { ColDef } from "ag-grid-community";
import { fromPairs } from "lodash";
import queryString from "query-string";
import * as React from "react";
import ReactPrismEditor from "react-prism-editor";
import { v4 as uuidv4 } from "uuid";

import {
  DEFAULT_CONFIG,
  InspectorRestConfig,
  PrefilterExpression,
  RequestTypes,
  useInspectorContext,
} from "../../context/inspector/context";
import {
  ContentProp,
  Line,
  ResponseContainer,
  ResponseTab,
} from "./InspectorContent";
import { PositionData } from "./ReportContent";

const getResponseTabs = (path: string) => {
  const tabs = [
    {
      label: "Body",
      key: "body",
    },
    {
      label: "Headers",
      key: "headers",
    },
  ];
  if (path?.includes("reports"))
    tabs.splice(1, 0, {
      label: "Tabular Data",
      key: "tabular-data",
    });
  return tabs;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getBody(body: string | FormData, otherData?: Record<string, any>) {
  if (body instanceof FormData) return body;
  try {
    const bodyObj = JSON.parse(body);
    return { ...otherData, ...bodyObj };
  } catch (err) {
    return body;
  }
}

export const RestContent: React.FC<ContentProp> = ({
  selectedParamTab,
  paramsOpen,
  setParamsOpen,
}) => {
  const [selectedMainTab, setSelectedMainTab] = React.useState<string>("body");
  const [gridData, setGridData] = React.useState<PositionData>({
    columnDefs: [],
    rowData: [],
  });

  const { setConfig, apiSource, config: configBase } = useInspectorContext();
  const { id } = useThisTab();
  const config = configBase as InspectorRestConfig;
  const {
    prefilterExpressions,
    predicate,
    groupPredicate,
    path,
    method,
    body,
    params,
    headers,
  } = config;

  const { response, loading, error, clear, call } = useFetchCalls();

  React.useEffect(() => {
    clear();
    setParamsOpen(true);
  }, [method, apiSource]);

  const fetchReportData = async (res: WebReportTableSync) => {
    if (res.tableMetadata) {
      const columnDefs: Array<ColDef> = createReportColumnDefs(res);
      const rowData =
        res?.rows?.map(
          (i, idx) =>
            ({
              ...i,
              __row_id: uuidv4(),
              __row_idx: idx,
            } as unknown as PositionFinderRowData)
        ) ?? [];
      setGridData({ columnDefs, rowData });
    } else {
      warningToast(
        "Unable to show tabular data for the requested report",
        "Result malformed"
      );
    }
  };

  const handleSend = useRefCallback(async () => {
    setSelectedMainTab("body");
    setGridData({
      columnDefs: [],
      rowData: [],
    });
    setParamsOpen(false);

    const filterBody = {
      expressions: prefilterExpressions,
      predicate,
      groupPredicate,
    };
    let requestBody = getBody(
      body,
      path.includes("reports") && prefilterExpressions?.length > 0
        ? filterBody
        : {}
    );
    requestBody = method !== "GET" ? requestBody : undefined;

    call(queryString.stringifyUrl({ url: path, query: params }), {
      method,
      headers,
      body: requestBody,
      onResponse: (res) => {
        if (path.includes("reports"))
          fetchReportData(res[0] as unknown as WebReportTableSync);
      },
    });
  }, [
    prefilterExpressions,
    predicate,
    groupPredicate,
    path,
    method,
    body,
    params,
    headers,
    call,
  ]);

  useBroadcastChannel("inspector-channel", (msg) => {
    const [msgId, type] = msg.split("<=>");
    if (msgId === id) {
      switch (type) {
        case "send":
          handleSend();
          break;
        case "cancel":
          clear();
          break;
        default:
          break;
      }
    }
  });

  const handleParamsChange = useRefCallback(
    (newParams: Array<[string, string]>) => {
      setConfig({ ...config, params: fromPairs(newParams) });
    },
    [config, setConfig]
  );
  const handleBodyContentChange = useRefCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value) setConfig({ ...config, body: e.target.value });
    },
    [config, setConfig]
  );
  const handleHeadersChange = useRefCallback(
    (newHeaders: Array<[string, string]>) => {
      setConfig({ ...config, headers: fromPairs(newHeaders) });
    },
    [config, setConfig]
  );
  const handlePreFilterChange = useRefCallback(
    (newFilter: Array<PrefilterExpression>) => {
      setConfig({ ...config, prefilterExpressions: newFilter });
    },
    [config, setConfig]
  );

  const bodyContent = React.useMemo(
    () => JSON.stringify(response?.[0] ?? {}, null, 2),
    [response]
  );

  return (
    <>
      <VisibleIf
        visibleStyle={{
          padding: "var(--spacing-xl)",
          height: 200,
          overflow: "auto",
        }}
        visible={paramsOpen && selectedParamTab !== "auth-token"}
      >
        <VisibleIf visible={selectedParamTab === "params"}>
          <KeyValueControl
            defaultValues={Object.entries(
              DEFAULT_CONFIG[RequestTypes.ENFUSION].params
            )}
            onChange={handleParamsChange}
          />
        </VisibleIf>
        <VisibleIf visible={selectedParamTab === "headers"}>
          <KeyValueControl
            defaultValues={Object.entries(headers)}
            onChange={handleHeadersChange}
          />
        </VisibleIf>
        <VisibleIf visible={selectedParamTab === "body"}>
          <TextInput
            name="text"
            hideLabel
            value={body}
            onChange={handleBodyContentChange}
            textarea
            style={{ height: "180px" }}
          />
        </VisibleIf>
        <VisibleIf visible={selectedParamTab === "pre-filter"}>
          <PreFilterReports
            defaultValues={
              DEFAULT_CONFIG[RequestTypes.REPORT].prefilterExpressions
            }
            onChange={handlePreFilterChange}
          />
        </VisibleIf>
      </VisibleIf>

      <Line />
      <ResponseTab>
        {loading ? (
          <CenterContent>
            <Spinner />
          </CenterContent>
        ) : error ? (
          <CenterContent>
            <ContentMessage message={error} />
          </CenterContent>
        ) : response ? (
          <>
            <SecondaryTabList
              value={selectedMainTab}
              onSelect={setSelectedMainTab}
              tabs={getResponseTabs(path)}
            />
            <ResponseContainer>
              <VisibleIf visible={selectedMainTab === "body"}>
                {bodyContent.split("\n").length > 10000 ? <><Alert type="warning" icon={faWarning} message="Response too long to format" /> <pre>{bodyContent}</pre></> : <ReactPrismEditor
                  language="json"
                  theme="tomorrow"
                  code={bodyContent}
                  lineNumber
                  readOnly
                  clipboard
                />}
              </VisibleIf>
              {gridData?.columnDefs?.length
                ? path.includes("report") && (
                    <VisibleIf visible={selectedMainTab === "tabular-data"}>
                      <TabularData
                        colDefs={gridData.columnDefs}
                        rowData={gridData.rowData || []}
                      />
                    </VisibleIf>
                  )
                : null}
              <VisibleIf visible={selectedMainTab === "headers"}>
                <TabularData
                  colDefs={[{ field: "name" }, { field: "value" }]}
                  rowData={[
                    ["url", response[1].url],
                    ["type", response[1].type],
                    ["status", response[1].status],
                    ["status text", response[1].statusText],
                    ["redirected", response[1].redirected],
                    ...response[1].headers.entries(),
                  ].map((e, idx) => ({
                    __row_id: idx,
                    name: e[0],
                    value: e[1],
                  }))}
                />
              </VisibleIf>
            </ResponseContainer>
          </>
        ) : (
          <CenterContent>
            <ContentMessage icon={faPaperPlane} message="Send Request" />
          </CenterContent>
        )}
      </ResponseTab>
    </>
  );
};
